import {
  abs,
  dot,
  float,
  floor,
  If,
  length,
  max,
  min,
  perspectiveDepthToViewZ,
  texture,
  vec2,
  vec3,
} from "three/tsl";
import type { Texture } from "three";

type Sampleable = {
  sample: (uv: unknown) => { r: unknown; rgb: { normalize: () => unknown } };
};

/**
 * 4 grid tap, 1/distance. needsFullGi && hasGi yoksa onFail = full GI.
 */
export function upsampleSparseGi(args: {
  giMap: Texture;
  screenCoordinate: { x: unknown; y: unknown };
  fullResolution: { x: unknown; y: unknown };
  stride: unknown;
  reversedDepth: unknown;
  depth?: unknown;
  normal?: unknown;
  depthThreshold: unknown;
  normalThreshold: unknown;
  minOkCount: unknown;
  cameraNear: unknown;
  cameraFar: unknown;
  screenUv: unknown;
  debug: unknown;
  sky: unknown;
  needsFullGi: unknown;
  onFail: () => unknown;
}): unknown {
  const s = float(args.stride as never);
  const markS = s;
  const fullW = float(args.fullResolution.x as never);
  const fullH = float(args.fullResolution.y as never);
  const px = args.screenCoordinate.x as { div: (n: unknown) => unknown };
  const py = args.screenCoordinate.y as { div: (n: unknown) => unknown };
  const x0 = (
    floor(px.div(s) as never) as { mul: (n: unknown) => unknown }
  ).mul(s);
  const y0 = (
    floor(py.div(s) as never) as { mul: (n: unknown) => unknown }
  ).mul(s);
  const x1 = min(
    (x0 as { add: (n: unknown) => unknown }).add(s) as never,
    fullW.sub(1) as never,
  );
  const y1 = min(
    (y0 as { add: (n: unknown) => unknown }).add(s) as never,
    fullH.sub(1) as never,
  );
  const pixel = vec2(
    args.screenCoordinate.x as never,
    args.screenCoordinate.y as never,
  );
  const ix = floor(args.screenCoordinate.x as never);
  const iy = floor(args.screenCoordinate.y as never);
  const gridDxMark = abs(
    (ix as { sub: (v: unknown) => unknown }).sub(
      (floor((ix as { div: (n: unknown) => unknown }).div(markS) as never) as {
        mul: (n: unknown) => unknown;
      }).mul(markS) as never,
    ) as never,
  );
  const gridDyMark = abs(
    (iy as { sub: (v: unknown) => unknown }).sub(
      (floor((iy as { div: (n: unknown) => unknown }).div(markS) as never) as {
        mul: (n: unknown) => unknown;
      }).mul(markS) as never,
    ) as never,
  );
  const onGridMark = (
    gridDxMark as { lessThan: (n: number) => { and: (o: unknown) => unknown } }
  )
    .lessThan(1.5)
    .and((gridDyMark as { lessThan: (n: number) => unknown }).lessThan(1.5));

  const selfSample = texture(args.giMap, args.screenUv as never) as {
    rgb: unknown;
    a: { greaterThan: (n: number) => unknown };
  };
  const selfGi = selfSample.rgb;
  const hasGi = selfSample.a.greaterThan(0.5) as {
    not: () => { and: (o: unknown) => unknown };
  };

  const corners = [
    [x0, y0],
    [x1, y0],
    [x0, y1],
    [x1, y1],
  ];

  const depth = args.depth as Sampleable | undefined;
  const normal = args.normal as Sampleable | undefined;
  const near = float(args.cameraNear as never);
  const far = float(args.cameraFar as never);
  const dThresh = float(args.depthThreshold as never);
  const nThresh = float(args.normalThreshold as never);

  const centerDepth = depth?.sample(args.screenUv).r;
  const centerViewZ =
    centerDepth != null
      ? perspectiveDepthToViewZ(centerDepth as never, near, far)
      : null;
  const centerN = depth && normal ? normal.sample(args.screenUv).rgb.normalize() : null;

  let sumW: unknown = float(0);
  let sumGi: unknown = vec3(0);
  let okCount: unknown = float(0);

  for (const [cx, cy] of corners) {
    const tapUv = vec2(
      (cx as { add: (n: number) => { div: (d: unknown) => unknown } })
        .add(0.5)
        .div(fullW) as never,
      (cy as { add: (n: number) => { div: (d: unknown) => unknown } })
        .add(0.5)
        .div(fullH) as never,
    );
    const tapCenter = vec2(
      (cx as { add: (n: number) => unknown }).add(0.5) as never,
      (cy as { add: (n: number) => unknown }).add(0.5) as never,
    );
    const dist = max(
      float(1e-3),
      length(
        (pixel as { sub: (c: unknown) => unknown }).sub(tapCenter) as never,
      ) as never,
    );
    const wDist = float(1).div(dist);
    const giSample = texture(args.giMap, tapUv as never) as {
      rgb: unknown;
      a: { greaterThan: (n: number) => unknown };
    };
    const gi = giSample.rgb;
    const hasTap = giSample.a.greaterThan(0.5);

    let keep: unknown = hasTap;
    if (depth && normal && centerViewZ != null && centerN != null) {
      const tapDepth = depth.sample(tapUv).r;
      const tapViewZ = perspectiveDepthToViewZ(tapDepth as never, near, far);
      const tapN = normal.sample(tapUv).rgb.normalize();
      const depthOk = abs(
        (centerViewZ as { sub: (v: unknown) => unknown }).sub(
          tapViewZ as never,
        ) as never,
      ).lessThan(dThresh as never);
      const normalOk = (
        dot(centerN as never, tapN as never) as {
          greaterThan: (n: unknown) => unknown;
        }
      ).greaterThan(nThresh);
      keep = (
        depthOk as { and: (o: unknown) => { and: (o: unknown) => unknown } }
      ).and(normalOk).and(hasTap);
    }

    const wUse = (keep as { select: (a: unknown, b: unknown) => unknown }).select(
      wDist,
      float(0),
    );
    okCount = (okCount as { add: (n: unknown) => unknown }).add(
      (keep as { select: (a: unknown, b: unknown) => unknown }).select(
        float(1),
        float(0),
      ),
    );
    sumW = (sumW as { add: (w: unknown) => unknown }).add(wUse);
    sumGi = (sumGi as { add: (v: unknown) => unknown }).add(
      (gi as { mul: (w: unknown) => unknown }).mul(wUse),
    );
  }

  const blended = (sumGi as { div: (w: unknown) => unknown }).div(
    max(sumW as never, float(1e-4)),
  );
  const fail = (
    hasGi.not() as { and: (o: unknown) => { and: (o: unknown) => unknown } }
  )
    .and(args.needsFullGi)
    .and((args.sky as { not: () => unknown }).not());
  const debugOn = (
    args.debug as { greaterThan: (n: unknown) => unknown }
  ).greaterThan(0.5);
  const out = vec3(0).toVar();
  (out as { assign: (v: unknown) => void }).assign(blended);
  If(hasGi as never, () => {
    (out as { assign: (v: unknown) => void }).assign(selfGi);
  });
  If(
    (fail as { and: (o: unknown) => unknown }).and(
      (debugOn as { not: () => unknown }).not() as never,
    ) as never,
    () => {
      (out as { assign: (v: unknown) => void }).assign(args.onFail());
    },
  );
  If(args.sky as never, () => {
    (out as { assign: (v: unknown) => void }).assign(vec3(0, 0, 0));
  });
  If(debugOn as never, () => {
    (out as { assign: (v: unknown) => void }).assign(vec3(0, 0, 0));
    If(
      (args.sky as { not: () => unknown }).not() as never,
      () => {
        If(
          (
            hasGi as { not: () => { and: (o: unknown) => unknown } }
          )
            .not()
            .and((fail as { not: () => unknown }).not()) as never,
          () => {
            (out as { assign: (v: unknown) => void }).assign(vec3(0, 1, 0));
          },
        );
        If(fail as never, () => {
          (out as { assign: (v: unknown) => void }).assign(vec3(1, 0, 0));
        });
        If(onGridMark as never, () => {
          (out as { assign: (v: unknown) => void }).assign(vec3(0, 0, 1));
        });
      },
    );
  });
  return out;
}
